import { createContext, useContext } from 'react';
import type { PaneId, PathResolver, WorkspaceState } from './types';

/*
  Контекст рабочего пространства и окна. Вынесен из компонентов, чтобы файлы компонентов
  экспортировали только компоненты (быстрая перезагрузка).
*/

/** Что тащат мышью: вкладку и окно, откуда она. */
export interface DragInfo {
  id: string;
  from: PaneId;
}

export interface WorkspaceApi {
  state: WorkspaceState;
  resolve: PathResolver;
  homePath: string;
  /** Открыть адрес: вид — во вкладке вида основного окна, объект — своей вкладкой. */
  go: (path: string, opts?: { pane?: PaneId; background?: boolean }) => void;
  /** Открыть объект во втором окне (или перенести туда уже открытый). */
  openBeside: (path: string, from?: PaneId) => void;
  activate: (pane: PaneId, id: string) => void;
  close: (pane: PaneId, id: string) => void;
  closeOthers: (pane: PaneId, id: string) => void;
  pin: (id: string, pinned: boolean) => void;
  move: (id: string, to: PaneId, index?: number) => void;
  unsplit: () => void;
  focus: (pane: PaneId) => void;
  setSplit: (ratio: number) => void;
  dragging: DragInfo | null;
  setDragging: (d: DragInfo | null) => void;
}

export const WorkspaceContext = createContext<WorkspaceApi | null>(null);

export function useWorkspace(): WorkspaceApi {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace: нужен WorkspaceProvider выше по дереву');
  return ctx;
}

/** Окно, внутри которого отрисован компонент: ссылки на объекты открываются в нём же. */
export const PaneContext = createContext<PaneId>('main');

export function usePane(): PaneId {
  return useContext(PaneContext);
}

/** Вкладка, внутри которой отрисован компонент: её id (VIEW_TAB — вкладка вида). Вне вкладок — null. */
export const TabContext = createContext<string | null>(null);

export function useTabId(): string | null {
  return useContext(TabContext);
}
